
import { store } from 'Models/Store';
import { LocaleCompareObjectAsc } from 'Util/StringUtils';
import { ComboboxOption } from 'Views/Components/Combobox/Combobox';
import { customerAuditCategoryOptions, userTypeOptions } from './Enums/MappedEnumOptions';

export type atbStatus =
	| 'CREATED'
	| 'VALIDATING'
	| 'VALIDATED'
	| 'QUEUED_FOR_SUBMISSION'
	| 'SUCCESS'
	| 'FAILED'
	| 'CANCELLED';

export type atbFileType =
	'XERO' |
	'STANDARD' |
	'UNKNOWN' |
	'CUSTOMERS_ONLY';

export const atbFileTypeOptions: { [key in atbFileType]: string } = {
	XERO: 'Xero',
	STANDARD: 'Standard',
	UNKNOWN: 'Unknown',
	CUSTOMERS_ONLY: 'Customers Only',
};


export type atoEntityType =
	'C' |
	'P' |
	'T' |
	'I';

export const atoEntityTypeOptions: { [key in atoEntityType]: string } = {
	C: 'Company',
	P: 'Partnership',
	T: 'Trust',
	I: 'Individual',
};

export type atoRecordStatus =
	'N' |
	'U' |
	'X' |
	'R' |
	'E';

export const atoRecordStatusOptions: { [key in atoRecordStatus]: string } = {
	N: 'New',
	U: 'Updated',
	X: 'Existing',
	R: 'Removed',
	E: 'Error',
};

export type accountType =
	'INVOICED' |
	'PAYG';


export type alaresCategory =
	'LICENCE' |
	'ASIC_DOCUMENT' |
	'INSOLVENCY' |
	'ADJUDICATION' |
	'ALARES_CASE';

export const alaresCategoryOptions: { [key in alaresCategory]: string } = {
	ADJUDICATION: 'Adjudication',
	ALARES_CASE: 'Case',
	ASIC_DOCUMENT: 'Asic',
	INSOLVENCY: 'Insolvency',
	LICENCE: 'Licence',
};

export type auditCategory =
	'CREDIT_DEFAULT' |
	'ADVERSE_ALERT' |
	'RISK_SCORE' |
	'ENTITY_UPDATE' |
	'OTHER';

export type creditReportStatus =
	'REQUESTED' |
	'GENERATED' |
	'FAILED' |
	'COMPLETED' |
	'COMPLETED_WITH_PROVIDER_ERROR' |
	'ERROR';

export const creditReportStatusOptions: { [key in creditReportStatus]: string } = {
	REQUESTED: 'Requested',
	GENERATED: 'Generated',
	FAILED: 'Failed',
	COMPLETED: 'Completed',
	COMPLETED_WITH_PROVIDER_ERROR: 'Completed (Credit Agency Issue)',
	ERROR: 'Error',
};

export type customerAuditCategory =
	'ACCOUNT_INFORMATION' |
	'ATO_DEFAULT' |
	'ADVERSE_ALERT' |
	'BUSINESS_INFORMATION' |
	'COMMENT' |
	'RISK_SCORE' |
	'REFERRED_FOR_COLLECTION' |
	'OTHER' |
	'ENTITY_UPDATE';


export type customerMonitoredStatus =
	'MONITORED' |
	'INSOLVENT' |
	'NOT_MONITORED' |
	'DELETED';

export const customerMonitoredStatusOptions: { [key in customerMonitoredStatus]: string } = {
	MONITORED: 'Monitored',
	INSOLVENT: 'Insolvent',
	NOT_MONITORED: 'Not Monitored',
	DELETED: 'Deleted',
};

export type discountSource =
	'CUSTOM' |
	'REFERRER' |
	'MIGRATION';

export const discountSourceOptions: { [key in discountSource]: string } = {
	CUSTOM: 'Custom',
	REFERRER: 'Referrer',
	MIGRATION: 'Migration',
};

export type esignceremonystatus =
	'CREATED' |
	'INCOMPLETE' |
	'COMPLETED' |
	'AWAITINGWITNESS' |
	'REJECTED' |
	'SIGNED' |
	'VISITED' |
	'SENT';

export const esignceremonystatusOptions: { [key in esignceremonystatus]: string } = {
	CREATED: 'Created',
	INCOMPLETE: 'Incomplete',
	COMPLETED: 'Completed',
	AWAITINGWITNESS: 'Awaiting Witness',
	REJECTED: 'Rejected',
	SIGNED: 'Signed',
	VISITED: 'Visited',
	SENT: 'Sent',
};

export type esignuserstatus =
	'ACTIVE' |
	'INACTIVE';

export const esignuserstatusOptions: { [key in esignuserstatus]: string } = {
	ACTIVE: 'Active',
	INACTIVE: 'Inactive',
};

export type esignwitnessstatus =
	'NOT_STARTED' |
	'SENT' |
	'VISITED' |
	'SIGNED' |
	'REJECTED';

export const esignwitnessstatusOptions: { [key in esignwitnessstatus]: string } = {
	NOT_STARTED: 'Not Started',
	SENT: 'Sent',
	VISITED: 'Visited',
	SIGNED: 'Signed',
	REJECTED: 'Rejected',
};

export type esignworkflowstage =
	'INITIATED' |
	'CEREMONYCREATED' |
	'SIGNATORYEMAILSENT' |
	'INPROGESS' |
	'COMPLETED';

export const esignworkflowstageOptions: { [key in esignworkflowstage]: string } = {
	// % protected region % [Override esignworkflowstage display fields here] on begin
	INITIATED: 'Initiated',
	CEREMONYCREATED: 'Ceremony Created',
	SIGNATORYEMAILSENT: 'Signatory Email Sent',
	INPROGESS: 'In Progess',
	COMPLETED: 'Completed',
};

export type esignworkflowstatus =
	'CREATED' |
	'PROCESSING' |
	'COMPLETED' |
	'INCOMPLETE' |
	'CANCELED' |
	'REJECTED';

// esignworkflowstatus both with an extra
export type aggregatedEsignWorkflowStatus = esignworkflowstatus | 'MIXED' | 'NONE';


export type organisationCategory =
	'PAYG' |
	'SMALL' |
	'MEDIUM' |
	'LARGE' |
	'ENTERPRISE';

export const organisationCategoryOptions: { [key in organisationCategory]: string } = {
	PAYG: 'Extra Small',
	SMALL: 'Small',
	MEDIUM: 'Medium',
	LARGE: 'Large',
	ENTERPRISE: 'Enterprise',
};

export type ppsrSearchType =
	'SERIAL_NUMBER' |
	'PPSR_NUMBER' |
	'ORGANISATION_GRANTOR' |
	'INDIVIDUAL_GRANTOR';

export const ppsrSearchTypeOptions: { [key in ppsrSearchType]: string } = {
	SERIAL_NUMBER: 'serial_number',
	PPSR_NUMBER: 'PPSR Number',
	ORGANISATION_GRANTOR: 'Organisation Grantor',
	INDIVIDUAL_GRANTOR: 'Individual Grantor',
};

export type processingStatus =
	'PROCESSING' |
	'PROCESSED' |
	'CREATED' |
	'ERROR';

export const processingStatusOptions: { [key in processingStatus]: string } = {
	PROCESSING: 'Processing',
	PROCESSED: 'Processed',
	CREATED: 'Created',
	ERROR: 'Error',
};

export type referrerBenefitType =
	'CHARITY' |
	'CLIENT_DISCOUNT' |
	'NO_COMMISSION' |
	'COMMISSION';

export const referrerBenefitTypeOptions: { [key in referrerBenefitType]: string } = {
	CHARITY: 'Charity',
	CLIENT_DISCOUNT: 'Client Discount',
	NO_COMMISSION: 'No Commission',
	COMMISSION: 'Commission',
};

export type referrerBusinessType =
	'LAWYER' |
	'INSOLVENCY_PRACTITIONER' |
	'DEBT_COLLECTION' |
	'CREDIT_BUREAU' |
	'CREDIT_INSURANCE_BROKER' |
	'INDIVIDUAL' |
	'OTHER';

export const referrerBusinessTypeOptions: { [key in referrerBusinessType]: string } = {
	LAWYER: 'Lawyer',
	INSOLVENCY_PRACTITIONER: 'Insolvency Practitioner',
	DEBT_COLLECTION: 'Debt Collection',
	CREDIT_BUREAU: 'Credit Bureau',
	CREDIT_INSURANCE_BROKER: 'Credit Insurance Broker',
	INDIVIDUAL: 'Individual',
	OTHER: 'Other',
};

export type referrerCommissionFrequency =
	'SIX_MONTHLY' |
	'YEARLY' |
	'QUARTERLY';

export const referrerCommissionFrequencyOptions: { [key in referrerCommissionFrequency]: string } = {
	SIX_MONTHLY: 'Six Monthly',
	YEARLY: 'Yearly',
	QUARTERLY: 'Quarterly',
};

export type registrationStatus =
	'CREATED' |
	'INCOMPLETE' |
	'COMPLETE' |
	'PENDING';

export const registrationStatusOptions: { [key in registrationStatus]: string } = {
	CREATED: 'Created',
	INCOMPLETE: 'Incomplete',
	COMPLETE: 'Complete',
	PENDING: 'Pending',
};

export type riskScore =
	'UNKNOWN' |
	'MINIMAL' |
	'VERY_LOW' |
	'LOW' |
	'AVERAGE' |
	'MODERATE' |
	'HIGH' |
	'VERY_HIGH' |
	'SEVERE';

export const riskScoreOptions: { [key in riskScore]: string } = {
	UNKNOWN: 'Unknown',
	MINIMAL: 'Minimal',
	VERY_LOW: 'Very Low',
	LOW: 'Low',
	AVERAGE: 'Average',
	MODERATE: 'Moderate',
	HIGH: 'High',
	VERY_HIGH: 'Very High',
	SEVERE: 'Severe',
};

export type state =
	'NONE' |
	'ACT' |
	'NSW' |
	'NT' |
	'QLD' |
	'SA' |
	'TAS' |
	'VIC' |
	'WA';

export const stateOptions: { [key in state]: string } = {
	NONE: 'None',
	ACT: 'ACT',
	NSW: 'NSW',
	NT: 'NT',
	QLD: 'QLD',
	SA: 'SA',
	TAS: 'TAS',
	VIC: 'VIC',
	WA: 'WA',
};

export type userType =
	'SALESPERSON' |
	'USER' |
	'ADMIN' |
	'ORGANISATION_MANAGER' |
	'SUPER_USER';

export type EntityNameType = 'MN' | 'DGR' | 'LGL' | 'TRD' | 'OTN' | 'BN';


export const UserTypeOrder: { [key in userType]: number } = {
	SALESPERSON: 0,
	USER: 1,
	ADMIN: 2,
	ORGANISATION_MANAGER: 3,
	SUPER_USER: 4,
};

// Return the options for a combobox for the usertype enum
export const getUserTypeComboboxOptions = () : ComboboxOption<string>[] => {

	const excluded = new Set<string>([
		// Dev note: exclude super user, unless you are a super user
		...(store.userType !== 'SUPER_USER' ? ['SUPER_USER'] : []),
	]);

	const values = Object.keys(userTypeOptions) as userType[];

	return values
		.filter(value => !excluded.has(value))
		.sort((a, b) => UserTypeOrder[b] - UserTypeOrder[a])
		.map<ComboboxOption<string>>(value => {
			return {
				display: userTypeOptions[value as userType],
				value: value,
				// Dev note: Should not be able to create a user type or switch to one that is above your owns
				disabled: UserTypeOrder[value as userType] > UserTypeOrder[store.userType],
			};
		});
};

// The enum describing what scope of userEntitys the permission belongs to
export type userPermissionScope =
	'SELF' |
	'WITHIN_BUSINESS_ENTITIES' |
	'WITHIN_ORGANISATION' |
	'ALL';

export const userPermissionScopeOptions: { [key in userPermissionScope]: string } = {
	SELF: 'Self',
	WITHIN_BUSINESS_ENTITIES: 'Within Business Entities',
	WITHIN_ORGANISATION: 'Within Organisation',
	ALL: 'All',
};

export const userPermissionScopeOrder: { [key in userPermissionScope]: number } = {
	SELF: 0,
	WITHIN_BUSINESS_ENTITIES: 1,
	WITHIN_ORGANISATION: 2,
	ALL: 3,
};

// Permission scope for organisations
export type organisationPermissionScope =
	'NONE' |
	'WITHIN_ORGANISATION' |
	'ALL';

export const organisationPermissionScopeOptions: { [key in organisationPermissionScope]: string } = {
	NONE: 'None',
	WITHIN_ORGANISATION: 'Within Organisation',
	ALL: 'All',
};

export const organisationPermissionScopeOrder: { [key in organisationPermissionScope]: number } = {
	NONE: 0,
	WITHIN_ORGANISATION: 1,
	ALL: 2,
};

// Permission scope for business entities
export type businessEntitiesPermissionScope =
	'NONE' |
	'WITHIN_ORGANISATION' |
	'WITHIN_BUSINESS_ENTITIES' |
	'ALL';

export const businessEntitiesPermissionScopeOptions: { [key in businessEntitiesPermissionScope]: string } = {
	NONE: 'None',
	WITHIN_BUSINESS_ENTITIES: 'Within Business Entity',
	WITHIN_ORGANISATION: 'Within Organisation',
	ALL: 'All',
};

export const businessEntitiesPermissionScopeOrder: { [key in businessEntitiesPermissionScope]: number } = {
	NONE: 0,
	WITHIN_BUSINESS_ENTITIES: 1,
	WITHIN_ORGANISATION: 2,
	ALL: 3,
};

/**
 * Function to return a list of the available scope options given the current scope option.
 * @param userScope The given scope of the permission
 * @param currentScope The current user's permission scope
 * @param permissionsScopeOrder A mapping for the scope enum values to an int value
 * @returns An array of available scope options given a specific permission scope
 */
const availablePermissionScopeOptions = (
	userScope: string,
	currentScope: string,
	permissionsScopeOrder: { [key in string]: number },
): string[] => {
	const scopeOptions: string[] = [];

	Object.keys(permissionsScopeOrder).forEach(option => {
		if (permissionsScopeOrder[userScope] >= permissionsScopeOrder[option]
			&& permissionsScopeOrder[currentScope] >= permissionsScopeOrder[option]) {
			scopeOptions.push(option);
		}
	});

	return scopeOptions;
};

// Return the options for a combobox for the userPermissionScope
export const permissionScopeCombobox = (
	type: 'user' | 'organisation' | 'businessEntity',
	permission?: userPermissionScope,
	currentPermission?: userPermissionScope,
	disabled?: boolean,
): ComboboxOption<string>[] => {
	let permissionScopeOptions: { [key in string]: string };
	let permissionScopeOrder: { [key in string]: number };
	switch (type) {
		case 'user':
			permissionScopeOptions = userPermissionScopeOptions;
			permissionScopeOrder = userPermissionScopeOrder;
			break;
		case 'organisation':
			permissionScopeOptions = organisationPermissionScopeOptions;
			permissionScopeOrder = organisationPermissionScopeOrder;
			break;
		case 'businessEntity':
			permissionScopeOptions = businessEntitiesPermissionScopeOptions;
			permissionScopeOrder = businessEntitiesPermissionScopeOrder;
			break;
	}

	let values = Object.keys(permissionScopeOptions);

	// given that the current permission scope is given and combobox is not disabled, reduce list
	if (permission && currentPermission && !disabled) {
		values = availablePermissionScopeOptions(permission, currentPermission, permissionScopeOrder);
	}

	return values
		.sort(LocaleCompareObjectAsc(permissionScopeOptions))
		.map<ComboboxOption<string>>((value, index, array) => {
			return { display: permissionScopeOptions[value], value: value };
		});
};


// Options in alphabetic order
export const registrationComboboxOptions: ComboboxOption<registrationStatus | 'ALL'>[] = [
	{ display: 'All Statuses', value: 'ALL' },
	{ display: 'Complete', value: 'COMPLETE' },
	{ display: 'Incomplete', value: 'INCOMPLETE' },
	{ display: 'Pending', value: 'PENDING' },
];

export const registrationWithCreatedComboboxOptions: ComboboxOption<registrationStatus | 'ALL'>[] = [
	{ display: 'All Statuses', value: 'ALL' },
	{ display: 'Complete', value: 'COMPLETE' },
	{ display: 'Created', value: 'CREATED' },
	{ display: 'Incomplete', value: 'INCOMPLETE' },
	{ display: 'Pending', value: 'PENDING' },
];

export const riskScoreComboboxOptions: ComboboxOption<riskScore | 'ALL'>[] = [
	{ display: 'All Risk Ratings', value: 'ALL' },
	{ display: 'Unknown', value: 'UNKNOWN' },
	{ display: 'Minimal', value: 'MINIMAL' },
	{ display: 'Very Low', value: 'VERY_LOW' },
	{ display: 'Low', value: 'LOW' },
	{ display: 'Average', value: 'AVERAGE' },
	{ display: 'Moderate', value: 'MODERATE' },
	{ display: 'High', value: 'HIGH' },
	{ display: 'Very High', value: 'VERY_HIGH' },
	{ display: 'Severe', value: 'SEVERE' },
];

export const customerMonitoredStatusComboboxOptions: ComboboxOption<customerMonitoredStatus | 'ALL'>[] = [
	{ display: 'All Monitored Statuses', value: 'ALL' },
	{ display: 'Monitored', value: 'MONITORED' },
	{ display: 'Insolvent', value: 'INSOLVENT' },
	{ display: 'Not Monitored', value: 'NOT_MONITORED' },
];

export const customerMonitoredStatusComboboxOptionsSuperuser: ComboboxOption<customerMonitoredStatus | 'ALL'>[] = [
	...customerMonitoredStatusComboboxOptions,
	{ display: 'Deleted', value: 'DELETED' },
]

// Tech debt: Don't use this. Use Enums.state. Will need to update the enum though.
// This one has 'ALL' and maps to different option texts// Options in alphabetic order
export type ausStates =
	'ALL' |
	'ACT' |
	'NSW' |
	'NT' |
	'QLD' |
	'SA' |
	'TAS' |
	'VIC' |
	'WA';

// Options in alphabetic order
export const ausStateOptions: { [key in ausStates]: string } = {
	ALL: 'All states',
	ACT: 'Australian Capital Territory',
	NSW: 'New South Wales',
	NT: 'Northern Territory',
	QLD: 'Queensland',
	SA: 'South Australia',
	TAS: 'Tasmania',
	VIC: 'Victoria',
	WA: 'Western Australia',
};

export const referrerBusinessTypeComboboxOptions: ComboboxOption<referrerBusinessType | 'ALL'>[] = [
	{ display: 'All Business Types', value: 'ALL' },
	{ display: 'Lawyer', value: 'LAWYER' },
	{ display: 'Debt Collection', value: 'DEBT_COLLECTION' },
	{ display: 'Insolvency Practitioner', value: 'INSOLVENCY_PRACTITIONER' },
	{ display: 'Credit Insurance Broker', value: 'CREDIT_INSURANCE_BROKER' },
	{ display: 'Credit Bureau', value: 'CREDIT_BUREAU' },
	{ display: 'Individual', value: 'INDIVIDUAL' },
	{ display: 'Other', value: 'OTHER' },
];

export const referrerBenefitTypeComboboxOptions: ComboboxOption<referrerBenefitType | 'ALL'>[] = [
	{ display: 'All Benefit Types', value: 'ALL' },
	{ display: 'Commission', value: 'COMMISSION' },
	{ display: 'Charity', value: 'CHARITY' },
	{ display: 'Client Discount', value: 'CLIENT_DISCOUNT' },
	{ display: 'No Commission', value: 'NO_COMMISSION' },
];

export const ausStateComboboxOptions: ComboboxOption<ausStates | 'ALL'>[] = (
	(Object.keys(ausStateOptions) as [ausStates | 'ALL'])
		.map<ComboboxOption<ausStates | 'ALL'>>((value, index, array) => {
			return {
				display: ausStateOptions[value],
				value: value,
			};
		})
);

export const acnClassOptions: { [key: string]: string } = {
	EQUT: 'Equity',
	LMGT: 'Limited by guarantee',
	LMSG: 'Limited by shares and guarantee',
	LMSH: 'Limited by shares',
	MORT: 'Mortgage',
	NLIA: 'No liability',
	NONE: 'Liability unknown',
	PROP: 'Property',
	UNKN: 'Liability unknown',
	UNLM: 'Unlimited',
};

export const acnSubClassOptions: { [key: string]: string } = {
	EXPN: 'Exempt proprietary company - non profit company',
	EXPS: 'Exempt proprietary and superannuation trustee company',
	EXPT: 'Exempt proprietary company',
	FXPT: 'Foreign company exempt from lodging balance sheet',
	HUNT: 'Proprietary - home unit company',
	LISN: 'Company licensed under Section 383 to omit \'Limited\' from its name',
	LISS: 'Company licensed under Section 383 to omit \'Limited\' from its name - superannuation trustee company',
	LIST: 'Listed public company',
	LXPT: 'Company licensed to omit \'Limited\' and exempt from returns',
	NEXN: 'Non-exempt proprietary company - non profit',
	NEXS: 'Non-exempt proprietary and superannuation trustee company',
	NEXT: 'Non-exempt proprietary company',
	NONE: 'Unknown',
	NLTD: 'Non-profit public company registered without \'Limited\' in its name under Section 150',
	PNPC: 'Proprietary - non-profit company',
	PSTC: 'Proprietary - superannuation trustee company',
	PUBF: 'Foreign company required to lodge a balance sheet',
	PROP: 'Proprietary - other',
	RACA: 'Registrable Australian corporation - association',
	RACO: 'Registrable Australian corporation - non association',
	SPEC: 'Special',
	ULSN: 'Unlisted public - non-profit company',
	ULSS: 'Unlisted public - superannuation trustee company',
	ULST: 'Unlisted public company',
};

export type ProcessingErrorType =
	'Internal'
	| 'Invalid'
	| 'Required'
	| 'CustomerNoMatch'
	| 'DuplicateDebtorIdWithinUploadMatch';
	
export type ProcessingErrorInputType =
	'None'
	| 'String'
	| 'Integer'
	| 'Currency'
	| 'Abn'
	| 'Acn'
	| 'Date'
	| 'Customer'
	| 'DebtorId';

export const riskScoreEnumToValueRange: { [key: string]: string[] } = {
	MINIMAL: ['0', '5'],
	VERY_LOW: ['5', '10'],
	LOW: ['10', '15'],
	AVERAGE: ['15', '20'],
	MODERATE: ['20', '50'],
	HIGH: ['50', '70'],
	VERY_HIGH: ['70', '90'],
	SEVERE: ['90', '1000000000'],
};


export type customerApplicationStatus =
	'PENDING' |
	'APPROVED' |
	'REFERRED' |
	'DECLINED' |
	'NOT_STARTED' |
	'INCOMPLETE' |
	'WITHDRAWN' |
	'UNKNOWN';

export type DataTypeSource = 'Live' | 'Internal' | 'Unavailable';

export type customerApplicationNoteType ='DECISION' | 'COMMENT'

export const combinedCustomerApplicationHistoryTypes: { [key in customerAuditCategory | customerApplicationNoteType]: string } = {
	...customerAuditCategoryOptions,
	COMMENT: 'Comment',
	DECISION: 'Decision'
};

export type CustomerCreationSource =
	'UNKNOWN' |
	'MONITOR_CUSTOMER' |
	'ATB_UPLOAD' |
	'CUSTOMER_UPLOAD';

export type CustomerApplicationTradeReferenceStatus =
	| "PENDING"
	| "SUBMITTED"
	| "DECLINED";

export type customerApplicationReviewPeriod =
	'NOT_REQUIRED' |
	'MONTHLY' |
	'QUARTERLY' |
	'SIX_MONTHLY' |
	'YEARLY';

export const customerApplicationReviewPeriodOptions: { [key in customerApplicationReviewPeriod]: string } = {
	NOT_REQUIRED: 'Not Required',
	MONTHLY: 'Monthly',
	QUARTERLY: 'Quarterly',
	SIX_MONTHLY: 'Six Monthly',
	YEARLY: 'Yearly'
};

export type SerialNumberType = 'VIN' | 'CHASSIS' | 'MANUFACTURERS_SERIAL';

export type WatercraftSerialNumberType = 'OFFICIAL_NUMBER' | 'HIN';

export type automatedPpsrGoniSource = 'ABN' | 'ACCOUNT_NUMBER' | 'APPLICATION_ID' | 'ABN_ENTITY_NAME';

